<script setup lang="ts">
import type { ItemLoginHistory } from '@/types/UserHistory';

const props = defineProps({
  currentPage: {
    type: [Number, String],
    default: 0,
  },
  perPage: {
    type: [Number, String],
    default: 0,
  },
  tableType: {
    type: String,
    default: '',
  },
  items: {
    type: Array<ItemLoginHistory>,
    default: () => [],
  },
});

const { t } = useTexts();
const loginHistoryFields = [
  {
    key: 'date',
    label: t('account.login_history.fields.date'),
  },
  {
    key: 'ip',
    label: t('account.login_history.fields.ip'),
  },
  {
    key: 'city_state',
    label: t('account.login_history.fields.city_state'),
  },
  {
    key: 'coordinates',
    label: t('account.login_history.fields.coordinates'),
  },
];

const defaultFields = [
  {
    key: 'type',
    label: t('account.wallet.table.fields.type'),
  },
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'created_at',
    label: t('account.wallet.table.fields.created_at'),
  },
];

const computedFields = computed(() => {
  if (!props.tableType) return [];

  if (props.tableType.match(/login-history/gi)) {
    return loginHistoryFields;
  }

  return defaultFields;
});

function dateFilter(date: string) {
  const dataFornecida = new Date(date);
  const fusoHorarioUsuario = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dataLocalUsuario = dataFornecida.toLocaleString(currentLocale(), {
    timeZone: fusoHorarioUsuario,
  });

  return dataLocalUsuario;
}

function fieldValue(value: string) {
  if (computedFields.value.filter((e) => e.key === value)?.length > 0) {
    return true;
  }

  return false;
}

function formatValue(value: string | null) {
  return value || '- - -';
}

function formatCoordinates(latitude: string | null, longitude: string | null) {
  return `
  ${`<div><span>Lat:</span> ${latitude ? formatValue(latitude) : '---'}</div>`}${`<div><span>Log:</span> ${longitude ? formatValue(longitude) : '---'}</div>`}`;
}
</script>

<template>
  <div :class="$style.userTableResponsive">
    <table
      v-if="computedFields && computedFields?.length"
      :class="$style.userTable"
    >
      <thead>
        <tr :class="$style.tableHeader">
          <th
            v-for="field in computedFields"
            :key="field.key"
          >
            {{ field.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="item in items"
          :key="item.date"
          :class="$style.tableItem"
        >
          <td v-if="fieldValue('date')">
            {{ dateFilter(item.date) }}
          </td>
          <td v-if="fieldValue('ip')">
            {{ formatValue(item.ip) }}
          </td>
          <td v-if="fieldValue('city_state')">
            {{ formatValue(item.city) }}
            {{ item.state ? `/ ${formatValue(item.state)}` : '' }}
          </td>
          <td v-if="fieldValue('state')">
            {{ formatValue(item.state) }}
          </td>
          <td v-if="fieldValue('coordinates')">
            <p
              :class="$style.coordinates"
              v-html="formatCoordinates(item.latitude, item.longitude)"
            ></p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style module lang="postcss">
.userTableResponsive {
  @apply w-full overflow-x-auto;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .userTable {
    @apply w-full gap-1 border-separate border-spacing-y-1.5;

    .tableHeader {
      th {
        @apply py-3 px-4 text-left text-xs font-semibold whitespace-nowrap bg-primary/5 text-primary/70;

        &:first-child {
          @apply rounded-l-md;
        }
        &:last-child {
          @apply rounded-r-md;
        }
      }
    }

    .tableItem {
      td {
        @apply py-3 px-3 text-left text-xs md:text-sm font-medium align-middle whitespace-nowrap bg-black/10;

        &:first-child {
          @apply rounded-l-md;
        }
        &:last-child {
          @apply rounded-r-md;
        }
      }

      &:nth-child(odd) {
        td {
          @apply bg-black/5;
        }
      }
    }

    .coordinates {
      @apply text-2xs leading-3;

      span {
        @apply opacity-50;
      }
    }

    .tableBadge {
      @apply text-xs font-semibold p-1 inline-flex rounded leading-3;

      &.success {
        @apply bg-success;
      }
      &.warning {
        @apply bg-warning text-black;
      }
      &.danger {
        @apply bg-error;
      }
      &.expired {
        @apply bg-info;
      }

      svg {
        @apply text-xs;
      }

      small {
        @apply leading-3 -my-px;
      }
    }
  }
}
</style>
